import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function NotificationManager({ token, onClose }) {
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchNotificationTypes();
  }, []);

  const fetchNotificationTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationTypes(data.notification_types || []);
        setCategories(data.categories || []);
        setRoles(data.roles || {});
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load notification types' });
    }
    setLoading(false);
  };

  const handleToggle = async (typeId, field, value) => {
    const formData = new FormData();
    formData.append(field, value);

    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/types/${typeId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        fetchNotificationTypes();
        setMessage({ type: 'success', text: 'Updated successfully' });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update' });
    }
  };

  const handleUpdateRecipients = async (typeId, updates) => {
    setSaving(true);
    const formData = new FormData();
    Object.entries(updates).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        formData.append(key, value.join(','));
      } else {
        formData.append(key, value);
      }
    });

    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/types/${typeId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        fetchNotificationTypes();
        setMessage({ type: 'success', text: 'Recipients updated' });
        setSelectedType(null);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update recipients' });
    }
    setSaving(false);
  };

  const handleTest = async (typeId) => {
    setMessage({ type: 'info', text: 'Sending test notification...' });
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/test/${typeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.sent) {
        setMessage({ type: 'success', text: `Test sent! (${data.total_sent} recipients)` });
      } else {
        setMessage({ type: 'error', text: data.reason || 'Test failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send test' });
    }
  };

  const filteredTypes = activeCategory === 'all' 
    ? notificationTypes 
    : notificationTypes.filter(nt => nt.category === activeCategory);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    padding: '1rem',
    marginBottom: '0.75rem',
    transition: 'all 0.2s'
  };

  const toggleStyle = (isOn) => ({
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    background: isOn ? '#059669' : '#CBD5E1',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s'
  });

  const toggleKnobStyle = (isOn) => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'white',
    position: 'absolute',
    top: '2px',
    left: isOn ? '22px' : '2px',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: '#F8FAFC',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
          color: 'white',
          padding: '1.5rem',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>🔔 Notification Manager</h2>
              <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '0.875rem' }}>
                Configure who receives notifications and when
              </p>
            </div>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.25rem'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {message && (
          <div style={{
            padding: '0.75rem 1rem',
            margin: '1rem 1rem 0',
            borderRadius: '8px',
            background: message.type === 'success' ? '#D1FAE5' : message.type === 'error' ? '#FEE2E2' : '#DBEAFE',
            color: message.type === 'success' ? '#065F46' : message.type === 'error' ? '#991B1B' : '#1E40AF',
            fontSize: '0.9rem'
          }}>
            {message.text}
          </div>
        )}

        {/* Category Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          padding: '1rem',
          borderBottom: '1px solid #E2E8F0',
          flexWrap: 'wrap',
          flexShrink: 0
        }}>
          <button
            onClick={() => setActiveCategory('all')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: 'none',
              background: activeCategory === 'all' ? '#003366' : 'white',
              color: activeCategory === 'all' ? 'white' : '#64748B',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            📋 All ({notificationTypes.length})
          </button>
          {categories.map(cat => {
            const count = notificationTypes.filter(nt => nt.category === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: activeCategory === cat.id ? '#003366' : 'white',
                  color: activeCategory === cat.id ? 'white' : '#64748B',
                  fontWeight: '500',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {cat.icon} {cat.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Notification Types List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
          {filteredTypes.map(nt => (
            <div key={nt.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{nt.icon}</span>
                    <h4 style={{ margin: 0, color: '#1E293B' }}>{nt.name}</h4>
                    {nt.is_system && (
                      <span style={{ 
                        background: '#E0F2FE', 
                        color: '#0369A1', 
                        fontSize: '0.65rem', 
                        padding: '0.1rem 0.4rem', 
                        borderRadius: '4px',
                        fontWeight: '600'
                      }}>
                        SYSTEM
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '0.25rem 0', color: '#64748B', fontSize: '0.85rem' }}>
                    {nt.description}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
                      👥 {nt.recipients?.roles?.length || 0} roles
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
                      ⏰ {nt.trigger?.type || 'immediate'}
                    </span>
                    {nt.threshold && (
                      <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
                        🎯 {nt.threshold.field} {nt.threshold.operator} {nt.threshold.value}
                      </span>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {/* Channel Icons */}
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <span 
                      style={{ 
                        opacity: nt.channels?.email ? 1 : 0.3,
                        cursor: 'pointer',
                        fontSize: '1.1rem'
                      }}
                      title={`Email ${nt.channels?.email ? 'ON' : 'OFF'}`}
                      onClick={() => handleToggle(nt.id, 'channels_email', !nt.channels?.email)}
                    >
                      📧
                    </span>
                    <span 
                      style={{ 
                        opacity: nt.channels?.telegram ? 1 : 0.3,
                        cursor: 'pointer',
                        fontSize: '1.1rem'
                      }}
                      title={`Telegram ${nt.channels?.telegram ? 'ON' : 'OFF'}`}
                      onClick={() => handleToggle(nt.id, 'channels_telegram', !nt.channels?.telegram)}
                    >
                      📱
                    </span>
                  </div>
                  
                  {/* Enable Toggle */}
                  <div 
                    style={toggleStyle(nt.enabled)}
                    onClick={() => handleToggle(nt.id, 'enabled', !nt.enabled)}
                    title={nt.enabled ? 'Enabled' : 'Disabled'}
                  >
                    <div style={toggleKnobStyle(nt.enabled)} />
                  </div>
                  
                  {/* Edit Button */}
                  <button
                    onClick={() => setSelectedType(nt)}
                    style={{
                      padding: '0.4rem 0.75rem',
                      background: '#F1F5F9',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      color: '#64748B'
                    }}
                  >
                    Edit
                  </button>
                  
                  {/* Test Button */}
                  <button
                    onClick={() => handleTest(nt.id)}
                    disabled={!nt.enabled}
                    style={{
                      padding: '0.4rem 0.75rem',
                      background: nt.enabled ? '#059669' : '#CBD5E1',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: nt.enabled ? 'pointer' : 'not-allowed',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      color: 'white'
                    }}
                  >
                    Test
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Edit Modal */}
        {selectedType && (
          <EditNotificationModal
            notification={selectedType}
            roles={roles}
            onSave={(updates) => handleUpdateRecipients(selectedType.id, updates)}
            onClose={() => setSelectedType(null)}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
}

// Edit Notification Modal Component
function EditNotificationModal({ notification, roles, onSave, onClose, saving }) {
  const [selectedRoles, setSelectedRoles] = useState(notification.recipients?.roles || []);
  const [specificUsers, setSpecificUsers] = useState((notification.recipients?.users || []).join(', '));
  const [departmentBased, setDepartmentBased] = useState(notification.recipients?.department_based || false);
  const [notifyCreator, setNotifyCreator] = useState(notification.recipients?.notify_creator || false);
  const [threshold, setThreshold] = useState(notification.threshold || null);

  const handleSave = () => {
    const updates = {
      recipient_roles: selectedRoles,
      recipient_users: specificUsers,
      department_based: departmentBased,
      notify_creator: notifyCreator
    };
    
    if (threshold) {
      updates.threshold_field = threshold.field;
      updates.threshold_operator = threshold.operator;
      updates.threshold_value = threshold.value;
    }
    
    onSave(updates);
  };

  const toggleRole = (roleKey) => {
    if (selectedRoles.includes(roleKey)) {
      setSelectedRoles(selectedRoles.filter(r => r !== roleKey));
    } else {
      setSelectedRoles([...selectedRoles, roleKey]);
    }
  };

  const rolesList = Object.entries(roles).sort((a, b) => b[1].level - a[1].level);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '550px',
        maxHeight: '85vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          background: '#F8FAFC',
          padding: '1.25rem',
          borderBottom: '1px solid #E2E8F0',
          position: 'sticky',
          top: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>{notification.icon}</span>
              <h3 style={{ margin: 0, color: '#1E293B' }}>{notification.name}</h3>
            </div>
            <button onClick={onClose} style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#64748B'
            }}>×</button>
          </div>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Roles Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem', color: '#003366' }}>👥 Recipient Roles</h4>
            <p style={{ margin: '0 0 0.75rem', color: '#64748B', fontSize: '0.85rem' }}>
              Select which roles should receive this notification
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {rolesList.map(([key, role]) => (
                <label 
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: selectedRoles.includes(key) ? '#EFF6FF' : '#F8FAFC',
                    border: selectedRoles.includes(key) ? '2px solid #3B82F6' : '1px solid #E2E8F0',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(key)}
                    onChange={() => toggleRole(key)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <div>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{role.name}</span>
                    <span style={{ 
                      display: 'block', 
                      fontSize: '0.75rem', 
                      color: '#64748B' 
                    }}>
                      Level {role.level}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Options */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem', color: '#003366' }}>⚙️ Options</h4>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <input
                type="checkbox"
                checked={departmentBased}
                onChange={(e) => setDepartmentBased(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Department-based routing</span>
                <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748B' }}>
                  Managers only receive notifications for their department
                </span>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                type="checkbox"
                checked={notifyCreator}
                onChange={(e) => setNotifyCreator(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Always notify creator</span>
                <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748B' }}>
                  The person who created the repair request
                </span>
              </div>
            </label>
          </div>

          {/* Specific Users */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 0.75rem', color: '#003366' }}>📧 Specific Users</h4>
            <textarea
              value={specificUsers}
              onChange={(e) => setSpecificUsers(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                minHeight: '70px',
                resize: 'vertical',
                fontSize: '0.9rem'
              }}
            />
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#64748B' }}>
              Comma-separated list of email addresses to always notify
            </p>
          </div>

          {/* Threshold (if applicable) */}
          {notification.threshold && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', color: '#003366' }}>🎯 Threshold</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={threshold?.field || ''}
                  onChange={(e) => setThreshold({...threshold, field: e.target.value})}
                  placeholder="Field"
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px'
                  }}
                />
                <select
                  value={threshold?.operator || 'gt'}
                  onChange={(e) => setThreshold({...threshold, operator: e.target.value})}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px'
                  }}
                >
                  <option value="gt">Greater than</option>
                  <option value="gte">Greater or equal</option>
                  <option value="lt">Less than</option>
                  <option value="lte">Less or equal</option>
                  <option value="eq">Equals</option>
                </select>
                <input
                  type="number"
                  value={threshold?.value || ''}
                  onChange={(e) => setThreshold({...threshold, value: parseFloat(e.target.value)})}
                  placeholder="Value"
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px'
                  }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#F1F5F9',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                color: '#64748B'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#003366',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: 'white',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationManager;
